import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { ClothRibbon } from '../player/Cloth';
import type { CircleCollider } from '../player/PlayerController';
import { patchMaterial } from '../render/materials';
import type { Environment } from './Environment';
import type { Terrain } from './Terrain';
import { damp, mulberry32 } from './noise';

export const HAIKU: string[][] = [
  ['Golden sea of grass —', 'the wind writes a single line', 'only I can read.'],
  ['Silver plumes bow low;', 'a stone Jizō keeps the path', 'older than the road.'],
  ['No sword drawn today.', 'The field hums with dragonflies,', 'the sun leans westward.'],
  ['A lantern waking —', 'somewhere a bell answers it', 'across the tall grass.'],
  ['My shadow runs long', 'through the susuki, ahead', 'of my tired feet.'],
  ['Evening wind grows still.', 'Every blade has said its prayer.', 'I set down my hat.'],
];

const SITES: Array<[number, number, number]> = [
  [-34, -58, 0.6],
  [96, -148, -0.4],
  [-152, -188, 0.9],
  [44, -318, 3.1],
  [212, 36, -1.9],
  [-128, 118, 2.4],
];

function stoneTexture(seed: number): THREE.CanvasTexture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const c = canvas.getContext('2d');
  if (!c) throw new Error('2D canvas unavailable');
  const rng = mulberry32(seed);
  c.fillStyle = '#7d786c';
  c.fillRect(0, 0, size, size);
  for (let i = 0; i < 2600; i++) {
    const v = 90 + rng() * 70;
    c.fillStyle = `rgba(${v},${v - 4},${v - 12},${0.08 + rng() * 0.18})`;
    const r = 1 + rng() * 5;
    c.beginPath();
    c.arc(rng() * size, rng() * size, r, 0, Math.PI * 2);
    c.fill();
  }
  // Lichen and moss patches.
  for (let i = 0; i < 70; i++) {
    const moss = rng() < 0.6;
    c.fillStyle = moss ? `rgba(84,92,48,${0.18 + rng() * 0.25})` : `rgba(170,160,112,${0.15 + rng() * 0.2})`;
    c.beginPath();
    c.ellipse(rng() * size, rng() * size, 4 + rng() * 18, 3 + rng() * 12, rng() * 3, 0, Math.PI * 2);
    c.fill();
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

function bannerTexture(): THREE.CanvasTexture {
  const w = 128;
  const h = 512;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const c = canvas.getContext('2d');
  if (!c) throw new Error('2D canvas unavailable');
  c.fillStyle = '#efe6d2';
  c.fillRect(0, 0, w, h);
  c.fillStyle = '#9b2318';
  c.fillRect(0, 0, w, 46);
  c.fillRect(0, h - 22, w, 22);
  c.fillStyle = '#1d1712';
  c.font = '84px "Shippori Mincho", "Hiragino Mincho ProN", "Yu Mincho", serif';
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  ['風', 'の', '道'].forEach((ch, i) => c.fillText(ch, w / 2, 130 + i * 118));
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

function roughen(geometry: THREE.BufferGeometry, amount: number, seed: number): THREE.BufferGeometry {
  const rng = mulberry32(seed);
  const pos = geometry.getAttribute('position');
  const map = new Map<string, number>();
  for (let i = 0; i < pos.count; i++) {
    const key = `${pos.getX(i).toFixed(3)},${pos.getY(i).toFixed(3)},${pos.getZ(i).toFixed(3)}`;
    let n = map.get(key);
    if (n === undefined) {
      n = (rng() - 0.5) * 2;
      map.set(key, n);
    }
    const x = pos.getX(i);
    const z = pos.getZ(i);
    const len = Math.hypot(x, z) || 1;
    pos.setXYZ(i, x + (x / len) * n * amount, pos.getY(i) + n * amount * 0.4, z + (z / len) * n * amount);
  }
  geometry.computeVertexNormals();
  return geometry;
}

/** Collapse static meshes that share a material into one mesh: one draw call and one shadow call each. */
function mergeStaticMeshes(group: THREE.Group): void {
  group.updateMatrixWorld(true);
  const toGroup = group.matrixWorld.clone().invert();
  const buckets = new Map<THREE.Material, THREE.Mesh[]>();
  group.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh) || Array.isArray(obj.material)) return;
    const list = buckets.get(obj.material) ?? [];
    list.push(obj);
    buckets.set(obj.material, list);
  });
  const relative = new THREE.Matrix4();
  for (const [material, meshes] of buckets) {
    if (meshes.length < 2) continue;
    let geometries = meshes.map((mesh) => mesh.geometry.clone().applyMatrix4(relative.multiplyMatrices(toGroup, mesh.matrixWorld)));
    if (geometries.some((g) => !g.index)) {
      geometries = geometries.map((g) => {
        if (!g.index) return g;
        const flat = g.toNonIndexed();
        g.dispose();
        return flat;
      });
    }
    const merged = new THREE.Mesh(mergeGeometries(geometries), material);
    merged.castShadow = meshes.some((mesh) => mesh.castShadow);
    merged.receiveShadow = meshes.some((mesh) => mesh.receiveShadow);
    for (const geometry of geometries) geometry.dispose();
    for (const mesh of meshes) {
      mesh.removeFromParent();
      mesh.geometry.dispose();
    }
    group.add(merged);
  }
}

export type ShrineState = 'dormant' | 'lighting' | 'lit';

export class Shrine {
  readonly group = new THREE.Group();
  readonly position = new THREE.Vector3();
  readonly front = new THREE.Vector3();
  readonly colliders: CircleCollider[] = [];
  readonly chimePosition = new THREE.Vector3();
  state: ShrineState = 'dormant';
  glow = 0;
  private glowTarget = 0;
  private readonly light: THREE.PointLight;
  private readonly flameMaterial: THREE.MeshStandardMaterial;
  readonly paper: ClothRibbon;
  private readonly paperAnchor = new THREE.Vector3();
  private chimeCooldown = 3;
  private flicker = Math.random() * 10;

  constructor(
    readonly index: number,
    x: number,
    z: number,
    facing: number,
    terrain: Terrain,
    materials: { stone: THREE.Material; darkStone: THREE.Material; cloth: THREE.Material; wood: THREE.Material; paper: THREE.Material; banner: THREE.Material },
  ) {
    const y = terrain.heightAt(x, z);
    this.position.set(x, y, z);
    this.group.position.copy(this.position);
    this.group.rotation.y = facing;
    this.front.set(Math.sin(facing), 0, Math.cos(facing));
    const seed = 100 + index * 17;
    const rng = mulberry32(seed);

    const add = (mesh: THREE.Mesh) => {
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.group.add(mesh);
      return mesh;
    };

    // Rough stone platform.
    const slab = add(new THREE.Mesh(roughen(new THREE.CylinderGeometry(0.95, 1.08, 0.22, 11, 2), 0.06, seed), materials.darkStone));
    slab.position.y = 0.02;

    // Jizō on a pedestal.
    const pedestal = add(new THREE.Mesh(roughen(new THREE.BoxGeometry(0.5, 0.2, 0.44, 3, 2, 3), 0.018, seed + 1), materials.stone));
    pedestal.position.set(0, 0.23, 0);
    const profile = [
      [0.0, 0.0], [0.19, 0.0], [0.205, 0.05], [0.2, 0.18], [0.185, 0.32], [0.16, 0.4], [0.115, 0.45], [0.075, 0.47], [0.0, 0.475],
    ].map(([r, h]) => new THREE.Vector2(r, h));
    const body = add(new THREE.Mesh(new THREE.LatheGeometry(profile, 20), materials.stone));
    body.position.y = 0.33;
    body.scale.set(1, 1, 0.82);
    const head = add(new THREE.Mesh(new THREE.SphereGeometry(0.105, 18, 14), materials.stone));
    head.position.set(0, 0.33 + 0.55, 0.01);
    head.scale.set(1, 1.08, 0.95);
    const bib = add(new THREE.Mesh(new THREE.CylinderGeometry(0.085, 0.2, 0.15, 18, 1, true, -Math.PI * 0.55, Math.PI * 1.1), materials.cloth));
    bib.position.set(0, 0.33 + 0.385, 0.012);
    bib.scale.set(1, 1, 0.9);
    const cap = add(new THREE.Mesh(new THREE.SphereGeometry(0.109, 16, 8, 0, Math.PI * 2, 0, Math.PI * 0.32), materials.cloth));
    cap.position.set(0, 0.33 + 0.565, 0.008);

    // Stone lantern (tōrō).
    const lantern = new THREE.Group();
    lantern.position.set(0.72, 0.1, 0.2);
    lantern.rotation.y = rng() * 0.6;
    this.group.add(lantern);
    const part = (geo: THREE.BufferGeometry, mat: THREE.Material, yPos: number) => {
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.y = yPos;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      lantern.add(mesh);
      return mesh;
    };
    part(roughen(new THREE.CylinderGeometry(0.2, 0.24, 0.12, 6), 0.01, seed + 2), materials.stone, 0.06);
    part(new THREE.CylinderGeometry(0.065, 0.08, 0.52, 8), materials.stone, 0.38);
    part(new THREE.CylinderGeometry(0.21, 0.15, 0.08, 6), materials.stone, 0.68);
    this.flameMaterial = new THREE.MeshStandardMaterial({ color: 0x1a120a, emissive: 0xff8a3a, emissiveIntensity: 0, roughness: 0.9 });
    const box = part(new THREE.BoxGeometry(0.22, 0.2, 0.22), materials.stone, 0.82);
    box.scale.set(1, 1, 1);
    const windowGeo = new THREE.PlaneGeometry(0.12, 0.11);
    for (let s = 0; s < 4; s++) {
      const win = new THREE.Mesh(windowGeo, this.flameMaterial);
      const a = (s * Math.PI) / 2;
      win.position.set(Math.sin(a) * 0.1101, 0.82, Math.cos(a) * 0.1101);
      win.rotation.y = a;
      lantern.add(win);
    }
    const roofGeo = new THREE.CylinderGeometry(0.03, 0.31, 0.15, 6, 1);
    const rp = roofGeo.getAttribute('position');
    for (let i = 0; i < rp.count; i++) {
      if (rp.getY(i) < 0 && Math.hypot(rp.getX(i), rp.getZ(i)) > 0.25) rp.setY(i, rp.getY(i) + 0.035);
    }
    roofGeo.computeVertexNormals();
    part(roofGeo, materials.stone, 1.0);
    part(new THREE.SphereGeometry(0.05, 10, 8), materials.stone, 1.11);
    part(new THREE.ConeGeometry(0.03, 0.06, 8), materials.stone, 1.17);

    this.light = new THREE.PointLight(0xff9a4a, 0, 11, 1.6);
    this.light.position.set(0, 0.82, 0);
    lantern.add(this.light);

    // Wind-chime post with a paper strip.
    const post = add(new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.035, 1.75, 6), materials.wood));
    post.position.set(-0.78, 0.1 + 0.875, -0.1);
    const bar = add(new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.035, 0.04), materials.wood));
    bar.position.set(-0.64, 1.78, -0.1);
    const bell = add(new THREE.Mesh(new THREE.SphereGeometry(0.045, 14, 10, 0, Math.PI * 2, 0, Math.PI * 0.62), new THREE.MeshPhysicalMaterial({ color: 0xd8efe8, roughness: 0.08, transmission: 0, transparent: true, opacity: 0.55, envMapIntensity: 1.4 })));
    bell.position.set(-0.5, 1.7, -0.1);
    this.group.updateMatrixWorld(true);
    this.chimePosition.set(-0.5, 1.7, -0.1).applyMatrix4(this.group.matrixWorld);
    this.paperAnchor.set(-0.5, 1.64, -0.1).applyMatrix4(this.group.matrixWorld);
    this.paper = new ClothRibbon(6, 0.34, 0.05, materials.paper, 1, 2.6, 1.6);

    // Nobori banner: a tall landmark that shows above the grass and waves in the wind.
    const pole = add(new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.028, 3.4, 6), materials.wood));
    pole.position.set(-1.25, 1.7, 0.45);
    const topBar = add(new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.014, 0.5, 5), materials.wood));
    topBar.rotation.z = Math.PI / 2;
    topBar.position.set(-1.25 + 0.24, 3.3, 0.45);
    const bannerGeo = new THREE.PlaneGeometry(0.46, 1.95, 8, 16);
    bannerGeo.translate(0.23, 0, 0);
    const banner = new THREE.Mesh(bannerGeo, materials.banner);
    banner.position.set(-1.24, 3.28 - 0.975, 0.45);
    banner.castShadow = true;
    this.group.add(banner);

    // Scattered stones.
    for (let i = 0; i < 5; i++) {
      const r = 0.08 + rng() * 0.16;
      const rock = add(new THREE.Mesh(roughen(new THREE.IcosahedronGeometry(r, 1), r * 0.25, seed + 10 + i), materials.darkStone));
      const a = rng() * Math.PI * 2;
      const d = 1.2 + rng() * 1.3;
      rock.position.set(Math.cos(a) * d, r * 0.3, Math.sin(a) * d);
      rock.rotation.set(rng() * 3, rng() * 3, rng() * 3);
      rock.scale.y = 0.6;
    }

    const toWorld = (lx: number, lz: number) => new THREE.Vector3(lx, 0, lz).applyMatrix4(this.group.matrixWorld);
    const statueW = toWorld(0, 0);
    const lanternW = toWorld(0.72, 0.2);
    const postW = toWorld(-0.78, -0.1);
    const poleW = toWorld(-1.25, 0.45);
    this.colliders.push(
      { x: statueW.x, z: statueW.z, r: 0.45 },
      { x: lanternW.x, z: lanternW.z, r: 0.3 },
      { x: postW.x, z: postW.z, r: 0.1 },
      { x: poleW.x, z: poleW.z, r: 0.08 },
    );
    mergeStaticMeshes(this.group);
  }

  ignite(): void {
    if (this.state !== 'dormant') return;
    this.state = 'lighting';
    this.glowTarget = 1;
  }

  update(dt: number, windVelocity: THREE.Vector3, gust: number): boolean {
    this.glow = damp(this.glow, this.glowTarget, 0.9, dt);
    if (this.state === 'lighting' && this.glow > 0.95) this.state = 'lit';
    this.flicker += dt;
    const f = 0.86 + Math.sin(this.flicker * 11.3) * 0.06 + Math.sin(this.flicker * 23.7) * 0.05 + Math.sin(this.flicker * 5.1) * 0.03;
    this.flameMaterial.emissiveIntensity = this.glow * 6 * f;
    this.light.intensity = this.glow * 5.5 * f;
    this.paper.update(dt, this.paperAnchor, new THREE.Vector3(0, -1, 0), new THREE.Vector3(1, 0, 0), windVelocity, []);
    this.chimeCooldown -= dt;
    if (gust > 0.58 && this.chimeCooldown <= 0) {
      this.chimeCooldown = 5 + Math.random() * 6;
      return true;
    }
    return false;
  }
}

export class Shrines {
  readonly group = new THREE.Group();
  readonly list: Shrine[] = [];

  constructor(terrain: Terrain, env: Environment) {
    const u = env.uniforms;
    const stoneTex = stoneTexture(3);
    const stone = patchMaterial(new THREE.MeshStandardMaterial({ map: stoneTex, color: 0xb9b4a6, roughness: 0.95, envMapIntensity: 0.6 }), u, { rim: 0.15 });
    const darkStone = patchMaterial(new THREE.MeshStandardMaterial({ map: stoneTex, color: 0x7f7a6c, roughness: 0.97, envMapIntensity: 0.5 }), u, { rim: 0.1 });
    const cloth = patchMaterial(new THREE.MeshPhysicalMaterial({ color: 0x8e1b14, roughness: 0.85, sheen: 1, sheenColor: new THREE.Color(0xd86a50), side: THREE.DoubleSide }), u, { rim: 0.4 });
    const wood = patchMaterial(new THREE.MeshStandardMaterial({ color: 0x4a3524, roughness: 0.85 }), u, { rim: 0.15 });
    const paper = patchMaterial(new THREE.MeshStandardMaterial({ color: 0xf2ead8, roughness: 0.8, side: THREE.DoubleSide }), u, { rim: 0.9 });
    const banner = patchMaterial(
      new THREE.MeshStandardMaterial({ map: bannerTexture(), roughness: 0.85, side: THREE.DoubleSide, envMapIntensity: 0.6 }),
      u,
      { rim: 0.8, wave: 0.11 },
    );
    SITES.forEach(([x, z, facing], index) => {
      const shrine = new Shrine(index, x, z, facing, terrain, { stone, darkStone, cloth, wood, paper, banner });
      terrain.setClearing(index, x, z, 3.3);
      this.list.push(shrine);
      this.group.add(shrine.group, shrine.paper.mesh);
    });
  }

  get colliders(): CircleCollider[] {
    return this.list.flatMap((s) => s.colliders);
  }

  get litCount(): number {
    return this.list.filter((s) => s.state !== 'dormant').length;
  }

  nearestDormant(from: THREE.Vector3): Shrine | null {
    let best: Shrine | null = null;
    let bestD = Infinity;
    for (const s of this.list) {
      if (s.state !== 'dormant') continue;
      const d = s.position.distanceToSquared(from);
      if (d < bestD) {
        bestD = d;
        best = s;
      }
    }
    return best;
  }
}
